/**
 * @fileoverview Error-state card for full-page HTTP error screens.
 * @description The white card shown inside AuthLayout on the /404, /403, and /500 routes: logo,
 * a colored icon badge, the status code, a heading/description pair, a "Go to Home" action with
 * an optional "Try Again" retry action, and a "Contact Support" link (navigates to the real
 * Contact Support page, see routes.constants.ts). "Go to Home" always resolves via
 * `resolveHomeRoute` (the same one RootRedirect uses), not a generic "/" — landing on one of these
 * pages usually means the visitor is already signed in, so send them straight back into whichever
 * module their privileges actually unlock rather than through the root redirect again. Mirrors
 * ForgotPasswordCard's logo/card chrome so every full-page screen in the app reads as one family.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import logoUrl from '@/assets/images/login/logo.png'
import Button from '@/shared/components/atoms/Button'
import LinkButton from '@/shared/components/atoms/LinkButton'
import { ROUTES } from '@/shared/constants/routes.constants'
import { resolveHomeRoute } from '@/shared/lib/auth/resolve-home-route'
import { useAuth } from '@/shared/lib/auth/useAuth'

interface ErrorStateCardProps {
  statusCode: string
  icon: string
  accent: 'primary' | 'error'
  title: string
  description: string
  showRetry?: boolean
  onRetry?: () => void
}

const ACCENT_CLASS_NAMES: Record<ErrorStateCardProps['accent'], { badge: string; code: string }> = {
  primary: { badge: 'bg-primary/10', code: 'text-primary' },
  error: { badge: 'bg-error/10', code: 'text-error' },
}

/**
 * @description Reload the current page — the default "Try Again" action, right when the card is
 * rendered inline on the page that failed (e.g. DashboardShell's session-load error), where the
 * URL is still that page's.
 */
function reloadPage() {
  window.location.reload()
}

/**
 * @component
 * @param {ErrorStateCardProps} props - The status code, icon, accent color, and copy to render.
 * @param {string} props.statusCode - The HTTP status code shown above the heading (e.g. "404").
 * @param {string} props.icon - URL of the badge's icon (imported SVG asset).
 * @param {'primary' | 'error'} props.accent - "primary" (blue, e.g. 404) or "error" (red, e.g.
 * 403/500) badge and status-code color.
 * @param {string} props.title - The error heading.
 * @param {string} props.description - The error description.
 * @param {boolean} [props.showRetry] - Shows a secondary "Try Again" button alongside "Go to
 * Home". Off by default.
 * @param {() => void} [props.onRetry] - What "Try Again" does. Defaults to reloading the current
 * page; a dedicated error route like /500 passes its own, since reloading it only re-renders the
 * error.
 */
function ErrorStateCard({
  statusCode,
  icon,
  accent,
  title,
  description,
  showRetry = false,
  onRetry = reloadPage,
}: ErrorStateCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const accentClassNames = ACCENT_CLASS_NAMES[accent]

  return (
    <div className="flex w-full flex-col items-center">
      <img src={logoUrl} alt="InsightBox" className="mb-2 h-18 sm:h-22 lg:h-24 w-auto" />
      <div className="mt-8 w-full rounded-[18px] border border-primary/10 bg-white p-6 text-center shadow-card sm:p-8 lg:p-10">
        <div
          className={`mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full ${accentClassNames.badge}`}
        >
          <img src={icon} alt="" className="h-7 w-7" />
        </div>
        <p className={`mb-1.5 text-[13px] font-bold tracking-[1.5px] ${accentClassNames.code}`}>
          {t('errors.statusPrefix')} {statusCode}
        </p>
        <h1 className="mb-2.5 text-[24px] font-extrabold leading-[1.3] text-ink">{title}</h1>
        <p className="mx-auto max-w-[320px] text-[14px] leading-[22px] text-slate">{description}</p>
        <div className="mt-7 flex flex-col items-center gap-3">
          <Button
            type="button"
            onClick={() => void navigate(resolveHomeRoute(user?.privileges ?? []))}
          >
            {t('actions.goHome')}
          </Button>
          {showRetry && (
            <Button type="button" variant="secondary" onClick={onRetry}>
              {t('actions.tryAgain')}
            </Button>
          )}
          <LinkButton size="sm" onClick={() => void navigate(ROUTES.CONTACT_SUPPORT)}>
            {t('legalNav.contactSupport')}
          </LinkButton>
        </div>
      </div>
    </div>
  )
}

export default ErrorStateCard
